import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { SosEntityLink } from "@/components/SosEntityLink";
import { BomComponent } from "../../queries/tracking-data/bom-by-component";
import { getCostTaxBreakdownColumnDefs } from "../CostTaxBreakdownColumns";
import { ExistingInventoryQuantityTableCell } from "../existing-inventory/ExistingInventoryQuantityTableCell";
import { LeadTimeCell } from "../LeadTimeCell";
import { PreferredVendorCell } from "../PreferredVendorCell";
import { PurchasingNoteTableRow, getPurchasingNoteColumnDef } from "../purchasing-progress/purchasing-note-column";
import { QuantityRequiredForJobCell } from "../QuantityRequiredForJobCell";
import { TaxCodeCell } from "./TaxCodeCell";

interface BomComponentTableRowBase extends BomComponent {
  rowId: string;
  /** For assemblies, this is undefined. For other items, this is `nonInventoryQuantity` */
  numToPurchase?: number;
  /** For assemblies, this is `nonInventoryQuantity`. For other items, this is undefined. */
  numToBuild?: number;
}

type BomComponentTableRowWithoutNotes = BomComponentTableRowBase;

interface BomComponentTableRowWithNotes extends BomComponentTableRowBase, PurchasingNoteTableRow {}

export type BomComponentTableRow = BomComponentTableRowWithoutNotes | BomComponentTableRowWithNotes;

const columnHelper = createColumnHelper<BomComponentTableRow>();

const itemDetailColumns = [
  columnHelper.accessor("item.name", {
    header: "Item",
    enableHiding: false,
    cell: ({ row }) => <SosEntityLink type="item" id={row.original.item.id} label={row.original.item.name} />,
  }),
  columnHelper.accessor("item.type", { header: "Type" }),
  columnHelper.accessor("item.description", { header: "Description" }),
  columnHelper.accessor("item.preferredVendor.id", {
    header: "Preferred Vendor",
    cell: ({ row }) => <PreferredVendorCell item={row.original.item} />,
  }),
  columnHelper.accessor("item.purchaseTaxCode", {
    header: "Tax Rate",
    cell: ({ row }) => <TaxCodeCell item={row.original.item} />,
  }),
  columnHelper.accessor("item.leadTimeMs", {
    header: "Lead Time",
    cell: ({ row }) => <LeadTimeCell item={row.original.item} />,
  }),
  columnHelper.accessor("onSalesOrder", {
    header: "On Sales Order",
    cell: ({ getValue }) => (getValue() ? "✔" : "❌"),
    meta: { cellProps: { className: "text-center align-middle" } },
  }),
];

const estimatedUnitCostColumns = getCostTaxBreakdownColumnDefs<BomComponentTableRow>((row) => row.unitCost, "unitCost");

const getEstimatedQuantitiesColumns = (jobId: number | null) =>
  [
    jobId !== null &&
      columnHelper.accessor("quantityRequiredForJob", {
        header: "Total Required",
        cell: ({ row }) => <QuantityRequiredForJobCell item={row.original.item} />,
      }),
    jobId !== null &&
      columnHelper.accessor("existingInventoryQuantity", {
        header: "# To/From Inventory",
        cell: ({ row }) => <ExistingInventoryQuantityTableCell item={row.original.item} fromRowId={row.id} />,
      }),
    columnHelper.accessor("numToPurchase", {
      header: "# To Purchase",
      cell: ({ getValue }) => getValue()?.toLocaleString(undefined, { maximumFractionDigits: 5 }) ?? "N/A",
      meta: { cellProps: ({ getValue }) => (getValue() === undefined ? { className: "text-body-tertiary" } : {}) },
    }),
    columnHelper.accessor("numToBuild", {
      header: "# To Build",
      cell: ({ getValue }) => getValue()?.toLocaleString(undefined, { maximumFractionDigits: 5 }) ?? "N/A",
      meta: { cellProps: ({ getValue }) => (getValue() === undefined ? { className: "text-body-tertiary" } : {}) },
    }),
  ].filter(Boolean) as ColumnDef<BomComponentTableRow>[];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const getBomByComponentTableColumns = (jobId: number | null): ColumnDef<BomComponentTableRow>[] =>
  [
    columnHelper.group({
      header: "Item Details",
      columns: itemDetailColumns,
    }),
    columnHelper.group({
      header: "Estimated Unit Cost",
      columns: estimatedUnitCostColumns,
    }),
    columnHelper.group({
      header: "Estimated Quantities",
      columns: getEstimatedQuantitiesColumns(jobId),
    }),
    jobId !== null &&
      (getPurchasingNoteColumnDef<Extract<BomComponentTableRow, PurchasingNoteTableRow>>({
        getItemId: (row) => row.item.id,
      }) as ColumnDef<BomComponentTableRow>),
  ].filter(Boolean) as ColumnDef<BomComponentTableRow>[];
